import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Mail,
  Calendar,
  Globe,
  Database,
  Lock,
  Eye,
  UserCheck,
  AlertTriangle,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SEO } from "../components/SEO";

export function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const lastUpdated = "January 26, 2026";
  const effectiveDate = "January 26, 2026";

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO />
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header */}
        <div className="bg-slate-900 text-white py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-4"
            >
              <div className="w-16 h-16 rounded-xl bg-teal-500/20 flex items-center justify-center">
                <Shield className="w-8 h-8 text-teal-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Privacy Policy</h1>
                <p className="text-slate-400 mt-1">Builder Buddy Canada</p>
              </div>
            </motion.div>
            <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-400">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Last Updated: {lastUpdated}
              </span>
              <span className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Effective Date: {effectiveDate}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="prose prose-slate max-w-none"
          >
            {/* Introduction */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Eye className="w-6 h-6 text-teal-600" />
                Introduction
              </h2>
              <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
                <p className="text-slate-700 leading-relaxed mb-4">
                  Builder Buddy Canada ("Builder Buddy," "we," "us," or "our"),
                  a division of Tam-Crete Engineering, is committed to
                  protecting your privacy and personal information. This Privacy
                  Policy explains how we collect, use, disclose, and safeguard
                  your information when you use our website, mobile application,
                  and services (collectively, the "Service").
                </p>
                <p className="text-slate-700 leading-relaxed mb-4">
                  By accessing or using our Service, you acknowledge that you
                  have read, understood, and agree to be bound by this Privacy
                  Policy. If you do not agree with the terms of this Privacy
                  Policy, please do not access or use our Service.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  This Privacy Policy applies to all users of the Service,
                  including visitors, registered users, and subscribers,
                  regardless of how they access or use the Service.
                </p>
              </div>
            </section>

            {/* Information We Collect */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Database className="w-6 h-6 text-teal-600" />
                Information We Collect
              </h2>

              <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">
                1. Information You Provide Directly
              </h3>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>
                  <strong>Account Information:</strong> Name, email address,
                  phone number, company name, professional credentials, and
                  password when you create an account.
                </li>
                <li>
                  <strong>Profile Information:</strong> Professional title,
                  areas of expertise, project preferences, and other information
                  you choose to add to your profile.
                </li>
                <li>
                  <strong>Project Information:</strong> Property addresses,
                  building specifications, permit applications, development
                  proposals, construction plans, and related documents you
                  upload or input.
                </li>
                <li>
                  <strong>Communication Data:</strong> Messages sent through our
                  Service, chat conversations with our AI assistant, support
                  requests, and feedback you provide.
                </li>
                <li>
                  <strong>Payment Information:</strong> Billing address and
                  payment method details when you subscribe to premium services
                  (processed securely by third-party payment processors).
                </li>
                <li>
                  <strong>Survey Responses:</strong> Information provided in
                  surveys, questionnaires, or forms related to quantity surveys,
                  feasibility studies, or other assessments.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">
                2. Information Collected Automatically
              </h3>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>
                  <strong>Usage Data:</strong> Pages visited, features used,
                  search queries, time spent on the Service, click patterns, and
                  navigation paths.
                </li>
                <li>
                  <strong>Device Information:</strong> Device type, operating
                  system, browser type and version, unique device identifiers,
                  and mobile network information.
                </li>
                <li>
                  <strong>Log Data:</strong> IP address, access times, referring
                  URLs, error logs, and system activity.
                </li>
                <li>
                  <strong>Location Data:</strong> Approximate geographic
                  location based on IP address, and precise location if you
                  enable location services for property-related features.
                </li>
                <li>
                  <strong>Cookies and Similar Technologies:</strong> We use
                  cookies, web beacons, pixels, and similar tracking
                  technologies to collect information about your interactions
                  with our Service.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">
                3. Information from Third Parties
              </h3>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>
                  <strong>Public Records:</strong> Property information, land
                  titles, zoning data, and permit records from municipal
                  databases and government sources.
                </li>
                <li>
                  <strong>Analytics Providers:</strong> Aggregated demographic
                  and usage information from analytics services.
                </li>
                <li>
                  <strong>Business Partners:</strong> Information from partners
                  who integrate with our Service or refer users to us.
                </li>
              </ul>
            </section>

            {/* How We Use Information */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <UserCheck className="w-6 h-6 text-teal-600" />
                How We Use Your Information
              </h2>
              <p className="text-slate-700 mb-4">
                We use the information we collect for the following purposes:
              </p>

              <div className="grid gap-4">
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-900">
                    Service Provision
                  </h4>
                  <p className="text-slate-600 text-sm mt-1">
                    To provide, maintain, and improve our Service, including
                    AI-powered code analysis, permit tracking, feasibility
                    studies, and quantity surveys.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-900">
                    Personalization
                  </h4>
                  <p className="text-slate-600 text-sm mt-1">
                    To customize your experience, provide relevant
                    recommendations, and remember your preferences.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-900">
                    Communication
                  </h4>
                  <p className="text-slate-600 text-sm mt-1">
                    To send you service updates, technical notices, security
                    alerts, support messages, and promotional communications
                    (with your consent).
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-900">
                    Analytics and Improvement
                  </h4>
                  <p className="text-slate-600 text-sm mt-1">
                    To analyze usage patterns, conduct research, and improve our
                    Service, algorithms, and AI models.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-900">
                    Legal Compliance
                  </h4>
                  <p className="text-slate-600 text-sm mt-1">
                    To comply with applicable laws, regulations, legal
                    processes, and governmental requests.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-900">Security</h4>
                  <p className="text-slate-600 text-sm mt-1">
                    To detect, prevent, and address fraud, abuse, security
                    risks, and technical issues.
                  </p>
                </div>
              </div>
            </section>

            {/* AI and Machine Learning */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Lock className="w-6 h-6 text-teal-600" />
                AI and Machine Learning Disclosure
              </h2>
              <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-amber-900 mb-2">
                      Important Notice About AI-Generated Content
                    </h4>
                    <p className="text-amber-800 text-sm leading-relaxed mb-3">
                      Builder Buddy uses artificial intelligence and machine
                      learning technologies to analyze building codes, provide
                      recommendations, and assist with construction-related
                      queries. Please understand that:
                    </p>
                    <ul className="list-disc pl-5 text-amber-800 text-sm space-y-1">
                      <li>
                        AI-generated responses are for informational purposes
                        only and should not be considered professional advice.
                      </li>
                      <li>
                        Your queries and interactions may be used to improve our
                        AI models (in anonymized and aggregated form).
                      </li>
                      <li>
                        AI responses should always be verified by qualified
                        professionals before making decisions.
                      </li>
                      <li>
                        We use third-party AI services (including OpenRouter) to
                        process queries, subject to their privacy policies.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Information Sharing */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Globe className="w-6 h-6 text-teal-600" />
                Information Sharing and Disclosure
              </h2>
              <p className="text-slate-700 mb-4">
                We may share your information in the following circumstances:
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">
                Service Providers
              </h3>
              <p className="text-slate-700 mb-4">
                We share information with third-party vendors, consultants, and
                service providers who perform services on our behalf, including
                hosting providers, payment processors, analytics services,
                customer support tools, and AI service providers. These
                providers are contractually obligated to protect your
                information.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">
                Business Transfers
              </h3>
              <p className="text-slate-700 mb-4">
                In connection with any merger, acquisition, reorganization, sale
                of assets, or bankruptcy, your information may be transferred to
                the acquiring entity. We will provide notice before your
                information becomes subject to a different privacy policy.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">
                Legal Requirements
              </h3>
              <p className="text-slate-700 mb-4">
                We may disclose your information if required by law, court
                order, or governmental authority, or when we believe disclosure
                is necessary to protect our rights, your safety, or the safety
                of others, investigate fraud, or respond to a government
                request.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">
                With Your Consent
              </h3>
              <p className="text-slate-700">
                We may share your information with third parties when you have
                given us explicit consent to do so.
              </p>
            </section>

            {/* Data Retention */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Database className="w-6 h-6 text-teal-600" />
                Data Retention
              </h2>
              <p className="text-slate-700 mb-4">
                We retain your personal information for as long as necessary to
                fulfill the purposes described in this Privacy Policy, unless a
                longer retention period is required or permitted by law.
                Specifically:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>
                  <strong>Account Data:</strong> Retained while your account is
                  active and for up to 7 years after account deletion for legal
                  and business purposes.
                </li>
                <li>
                  <strong>Project Data:</strong> Retained for the duration of
                  your account plus 7 years to comply with professional
                  liability requirements.
                </li>
                <li>
                  <strong>Chat History:</strong> Retained for up to 2 years for
                  service improvement and support purposes.
                </li>
                <li>
                  <strong>Usage Data:</strong> Aggregated and anonymized data
                  may be retained indefinitely for analytics.
                </li>
                <li>
                  <strong>Legal Records:</strong> Information required for legal
                  compliance may be retained as required by applicable laws.
                </li>
              </ul>
            </section>

            {/* Data Security */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Lock className="w-6 h-6 text-teal-600" />
                Data Security
              </h2>
              <p className="text-slate-700 mb-4">
                We implement appropriate technical and organizational security
                measures to protect your information, including:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Encryption of data in transit (TLS/SSL) and at rest</li>
                <li>
                  Secure authentication mechanisms including password hashing
                </li>
                <li>Regular security assessments and penetration testing</li>
                <li>
                  Access controls and employee training on data protection
                </li>
                <li>
                  Incident response procedures for potential data breaches
                </li>
                <li>Regular backups and disaster recovery planning</li>
              </ul>
              <p className="text-slate-700 mt-4">
                However, no method of transmission over the Internet or
                electronic storage is 100% secure. While we strive to protect
                your information, we cannot guarantee absolute security.
              </p>
            </section>

            {/* Your Rights */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <UserCheck className="w-6 h-6 text-teal-600" />
                Your Rights and Choices
              </h2>
              <p className="text-slate-700 mb-4">
                Depending on your location, you may have the following rights
                regarding your personal information:
              </p>
              <div className="grid gap-3">
                <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                  <span className="font-bold text-teal-600">Access:</span>
                  <span className="text-slate-700">
                    Request a copy of the personal information we hold about
                    you.
                  </span>
                </div>
                <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                  <span className="font-bold text-teal-600">Correction:</span>
                  <span className="text-slate-700">
                    Request correction of inaccurate or incomplete personal
                    information.
                  </span>
                </div>
                <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                  <span className="font-bold text-teal-600">Deletion:</span>
                  <span className="text-slate-700">
                    Request deletion of your personal information, subject to
                    legal retention requirements.
                  </span>
                </div>
                <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                  <span className="font-bold text-teal-600">Portability:</span>
                  <span className="text-slate-700">
                    Request a machine-readable copy of your data for transfer to
                    another service.
                  </span>
                </div>
                <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                  <span className="font-bold text-teal-600">Opt-Out:</span>
                  <span className="text-slate-700">
                    Opt out of marketing communications and certain data
                    processing activities.
                  </span>
                </div>
                <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                  <span className="font-bold text-teal-600">
                    Withdraw Consent:
                  </span>
                  <span className="text-slate-700">
                    Withdraw consent previously provided, where processing is
                    based on consent.
                  </span>
                </div>
              </div>
              <p className="text-slate-700 mt-4">
                To exercise these rights, please contact us using the
                information provided below. We will respond to your request
                within the timeframe required by applicable law.
              </p>
            </section>

            {/* Canadian Privacy Laws */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-teal-600" />
                Canadian Privacy Law Compliance
              </h2>
              <p className="text-slate-700 mb-4">
                Builder Buddy is headquartered in Calgary, Alberta, Canada, and
                we comply with applicable Canadian privacy legislation,
                including:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>
                  <strong>
                    Personal Information Protection and Electronic Documents Act
                    (PIPEDA):
                  </strong>{" "}
                  Federal privacy law governing the collection, use, and
                  disclosure of personal information in commercial activities.
                </li>
                <li>
                  <strong>Personal Information Protection Act (PIPA):</strong>{" "}
                  Alberta's provincial privacy legislation for private sector
                  organizations.
                </li>
                <li>
                  <strong>Canada's Anti-Spam Legislation (CASL):</strong> We
                  comply with CASL requirements for electronic communications.
                </li>
              </ul>
            </section>

            {/* International Transfers */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Globe className="w-6 h-6 text-teal-600" />
                International Data Transfers
              </h2>
              <p className="text-slate-700">
                Your information may be transferred to and processed in
                countries other than Canada, including the United States, where
                our service providers may be located. These countries may have
                data protection laws that differ from Canadian laws. When we
                transfer your information internationally, we implement
                appropriate safeguards to protect your information in accordance
                with this Privacy Policy and applicable law, including
                contractual protections with our service providers.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <UserCheck className="w-6 h-6 text-teal-600" />
                Children's Privacy
              </h2>
              <p className="text-slate-700">
                Our Service is not intended for individuals under the age of 18.
                We do not knowingly collect personal information from children.
                If we become aware that we have collected personal information
                from a child without parental consent, we will take steps to
                delete that information. If you believe we may have information
                from or about a child, please contact us.
              </p>
            </section>

            {/* Changes to Policy */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Calendar className="w-6 h-6 text-teal-600" />
                Changes to This Privacy Policy
              </h2>
              <p className="text-slate-700">
                We may update this Privacy Policy from time to time. We will
                notify you of material changes by posting the updated policy on
                our website with a new "Last Updated" date, and where required
                by law, we will obtain your consent. We encourage you to review
                this Privacy Policy periodically. Your continued use of the
                Service after changes are posted constitutes your acceptance of
                the updated policy.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Mail className="w-6 h-6 text-teal-600" />
                Contact Us
              </h2>
              <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
                <p className="text-slate-700 mb-4">
                  If you have questions about this Privacy Policy, wish to
                  exercise your privacy rights, or have concerns about our data
                  practices, please contact us:
                </p>
                <div className="space-y-3 text-slate-700">
                  <p>
                    <strong>Builder Buddy Canada</strong>
                    <br />A division of Tam-Crete Engineering
                  </p>
                  <p>
                    <strong>Address:</strong> Calgary, Alberta, Canada
                  </p>
                  <p>
                    <strong>Phone:</strong> (403) 333-5025
                  </p>
                  <p>
                    <strong>Email:</strong> privacy@builderbuddy.ca
                  </p>
                  <p>
                    <strong>Website:</strong> https://builderbuddy.ca
                  </p>
                </div>
                <p className="text-slate-600 text-sm mt-4">
                  For privacy-related inquiries, please include "Privacy
                  Request" in the subject line of your email.
                </p>
              </div>
            </section>
          </motion.div>
        </div>

        {/* Footer */}
        <div className="bg-slate-100 border-t border-slate-200 py-6 sm:py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <p className="text-slate-600 text-sm">
              &copy; {new Date().getFullYear()} Builder Buddy Canada. All rights
              reserved.
            </p>
            <div className="mt-4 flex justify-center gap-4 sm:gap-6 text-sm">
              <button
                onClick={() => navigate("/terms")}
                className="text-teal-600 hover:text-teal-700"
              >
                Terms of Service
              </button>
              <button
                onClick={() => navigate("/")}
                className="text-teal-600 hover:text-teal-700"
              >
                Home
              </button>
              <button
                onClick={() => navigate("/chat")}
                className="text-teal-600 hover:text-teal-700"
              >
                AI Chat
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
